import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  TRIAGE_BUCKET_LABEL,
  TRIAGE_BUCKET_TONE,
  formatTriageDetected,
} from "../../utils/careTriageModel";
import { fetchCareTriageAssignees } from "../../utils/api";
import ClientAvatar from "../ClientAvatar";
import "./AssignFollowUpPanel.css";

const ACTION_TYPES = [
  "Contact client/family",
  "RN reassessment",
  "Supervisory visit",
  "Review care plan",
  "Contact physician",
  "Review device data",
  "Other",
];

const PRIORITIES = [
  { id: "high", label: "High" },
  { id: "normal", label: "Normal" },
  { id: "low", label: "Low" },
];

export default function AssignFollowUpPanel({
  row,
  locationId,
  onClose,
  onAssign,
  saving = false,
}) {
  const [assignToUserId, setAssignToUserId] = useState("");
  const [assignTo, setAssignTo] = useState("");
  const [actionType, setActionType] = useState(ACTION_TYPES[0]);
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState(row?.priority === "high" ? "high" : "normal");
  const [notes, setNotes] = useState("");
  const [groups, setGroups] = useState([]);
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [assigneeError, setAssigneeError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!locationId) {
      setGroups([]);
      setAssigneeError("Select a location to load your care team.");
      return undefined;
    }

    async function load() {
      setLoadingAssignees(true);
      setAssigneeError("");
      try {
        const data = await fetchCareTriageAssignees(locationId);
        if (cancelled) return;
        setGroups(data.groups || []);
        if (!(data.assignees || []).length) {
          setAssigneeError(
            "No caregivers, nurses, or staff are linked to this location yet. Add them under Admin → Caregivers, Nurses, or Users.",
          );
        }
      } catch (err) {
        if (!cancelled) {
          setAssigneeError(
            err?.response?.data?.message || "Could not load care team for this location.",
          );
          setGroups([]);
        }
      } finally {
        if (!cancelled) setLoadingAssignees(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  const assigneeById = useMemo(() => {
    const map = new Map();
    groups.forEach((g) => {
      (g.assignees || []).forEach((a) => map.set(a.id, a));
    });
    return map;
  }, [groups]);

  if (!row) return null;

  const bucketLabel = TRIAGE_BUCKET_LABEL[row.bucket] || "Triage";
  const tone = TRIAGE_BUCKET_TONE[row.bucket] || "gray";
  const change = Number(row.change);
  const changeLabel =
    row.change != null && change !== 0 ? ` (${change < 0 ? "↓" : "↑"}${Math.abs(change)})` : "";
  const subLine = [row.age ? `${row.age} yrs` : null, row.clientShortId].filter(Boolean).join(" · ");
  const canSubmit = !saving && assignToUserId && assignTo.trim() && due;

  function handleAssigneeChange(e) {
    const id = e.target.value;
    setAssignToUserId(id);
    const person = assigneeById.get(id);
    setAssignTo(person?.name || "");
  }

  return (
    <div className="assign-panel-backdrop" role="presentation" onClick={onClose}>
      <div
        className="assign-panel"
        role="dialog"
        aria-labelledby="assign-panel-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="assign-panel__top">
          <h2 id="assign-panel-title" className="assign-panel__title">
            Assign Follow-Up
          </h2>
          <button type="button" className="assign-panel__close" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="assign-panel__client-row">
          <ClientAvatar
            client={row}
            className="assign-panel__avatar"
            initialClassName="assign-panel__avatar-initial"
            size={40}
            alt={row.clientName}
          />
          <div className="assign-panel__client-text">
            <strong>{row.clientName}</strong>
            {subLine ? <span>{subLine}</span> : null}
          </div>
          <div className="assign-panel__badges">
            <span className={`assign-panel__badge assign-panel__badge--${tone}`}>
              AIP {row.aipCurrent ?? "—"}
              {changeLabel}
            </span>
            <span className={`assign-panel__badge assign-panel__badge--${tone}`}>{bucketLabel}</span>
          </div>
        </div>

        <div className={`assign-panel__event assign-panel__event--${tone}`}>
          <strong>Event: {row.note || "—"}</strong>
          <span>
            Detected: {formatTriageDetected(row.changeDate)} · Source: {row.source}
          </span>
        </div>

        <div className="assign-panel__grid">
          <label className="assign-panel__field">
            <span>
              Assign To <em aria-hidden="true">*</em>
            </span>
            <select
              id="assign-panel-assignee"
              value={assignToUserId}
              onChange={handleAssigneeChange}
              disabled={loadingAssignees || saving || !locationId}
              required
            >
              <option value="">
                {loadingAssignees ? "Loading care team…" : "Select caregiver, nurse, or staff…"}
              </option>
              {groups.map((group) => (
                <optgroup key={group.id} label={group.label}>
                  {(group.assignees || []).map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.name}
                      {person.email ? ` · ${person.email}` : ""}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {assigneeError ? (
              <span className="assign-panel__hint assign-panel__hint--error" role="status">
                {assigneeError}
              </span>
            ) : (
              <span className="assign-panel__hint">
                Caregivers, nurses, and staff linked to your current location.
              </span>
            )}
          </label>
          <label className="assign-panel__field">
            <span>
              Action Type <em aria-hidden="true">*</em>
            </span>
            <select value={actionType} onChange={(e) => setActionType(e.target.value)}>
              {ACTION_TYPES.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
          <label className="assign-panel__field">
            <span>
              Due Date &amp; Time <em aria-hidden="true">*</em>
            </span>
            <input
              type="datetime-local"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              required
            />
          </label>
          <fieldset className="assign-panel__field assign-panel__priority">
            <legend>Priority</legend>
            <div className="assign-panel__radios">
              {PRIORITIES.map((p) => (
                <label key={p.id} className={`assign-panel__radio assign-panel__radio--${p.id}`}>
                  <input
                    type="radio"
                    name="assign-priority"
                    value={p.id}
                    checked={priority === p.id}
                    onChange={() => setPriority(p.id)}
                  />
                  {p.label}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="assign-panel__field assign-panel__field--wide">
            <span>Notes (optional)</span>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Contact client to assess mobility and appetite. Follow up with family."
            />
          </label>
        </div>

        <div className="assign-panel__actions">
          <button type="button" className="assign-panel__cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="assign-panel__submit"
            disabled={!canSubmit}
            onClick={() =>
              onAssign?.({ assignTo, assignToUserId, actionType, due, priority, notes })
            }
          >
            {saving ? "Assigning…" : "Assign Case"}
          </button>
        </div>
      </div>
    </div>
  );
}
